import React from 'react';
import PageMeta from '../components/PageMeta';

const sections = [
  {
    title: '1. Subscription Fees',
    body: [
      'All subscription fees paid for BTree memberships are non-refundable except in the circumstances described below.',
    ],
  },
  {
    title: '2. Eligible Refund Situations',
    body: [
      'Refunds may be considered only in the following cases:',
    ],
    bullets: [
      'Duplicate payment due to technical error.',
      'Multiple subscriptions purchased unintentionally.',
      'Billing error attributable to BTree.',
      'Failure to activate the purchased subscription despite successful payment.',
    ],
    closing: 'Any approved refund will be processed to the original payment method.',
  },
  {
    title: '3. Non-Refundable Situations',
    body: [
      'Refunds will not be provided for:',
    ],
    bullets: [
      'Change of mind.',
      'Failure to use the subscription.',
      'Partial usage of a subscription period.',
      'Dissatisfaction with market performance, legal developments, sector developments or outcomes discussed in reports.',
      'User misunderstanding of the scope of services.',
      'Subscription cancellation after access has been granted.',
    ],
  },
  {
    title: '4. Refund Requests',
    body: [
      'Eligible refund requests must be submitted within 7 days of the transaction date.',
      'Requests should be sent to contact@thebtree.com with the following details:',
    ],
    bullets: [
      'Name',
      'Registered email address',
      'Transaction reference',
      'Description of the issue',
    ],
  },
  {
    title: '5. Processing Time',
    body: [
      'Approved refunds are generally processed within 7-14 business days depending on the payment provider and banking institution.',
    ],
  },
  {
    title: '6. Changes',
    body: [
      'BTree reserves the right to modify this Refund Policy at any time. Updated versions will be published on the website.',
    ],
  },
];

const RefundPolicy = () => {
  return (
    <>
      <PageMeta
        title="Refund Policy | The B Tree"
        description="Review B Tree’s refund conditions, eligible scenarios, and processing timeline for digital subscriptions."
        canonical="https://www.thebtree.com/refund"
        image="https://www.thebtree.com/logo.png"
        type="website"
      />
      <main className="min-h-screen bg-[#F7F5F2]" data-testid="refund-policy-page">
      <section className="bg-gradient-to-b from-[#FDFCFB] to-[#F7F5F2] border-b border-[#C8A84B]/20">
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-14 md:py-18">
          <p className="text-[11px] font-mono tracking-[0.3em] uppercase text-[#C8A84B] font-semibold mb-3">
            BTree Policies
          </p>
          <h1 className="text-4xl md:text-6xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] leading-[1.05] mb-4">
            BTree Refund Policy
          </h1>
          <p className="text-sm md:text-base text-slate-600">
            Effective Date: June 15, 2026
          </p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16 py-12 md:py-16">
        <article className="bg-white border border-slate-200 rounded-2xl p-6 md:p-10 shadow-sm">
          <p className="text-lg leading-8 text-slate-700 mb-10">
            The BTree Company provides digital subscription-based access to proprietary intelligence reports,
            research publications, educational content and related digital services.
          </p>

          <p className="text-lg leading-8 text-slate-700 mb-12">
            As all products and services offered by BTree are digital in nature and become accessible immediately
            upon subscription activation, refunds are generally not available once access has been granted.
          </p>

          <div className="space-y-10">
            {sections.map((section) => (
              <section key={section.title}>
                <h2 className="text-2xl md:text-3xl font-[Cormorant_Garamond] font-medium text-[#0D1B2A] mb-3">
                  {section.title}
                </h2>

                {section.body.map((paragraph) => (
                  <p key={paragraph} className="text-slate-700 leading-7 mb-4">
                    {section.title === '4. Refund Requests' && paragraph.includes('contact@thebtree.com') ? (
                      <>
                        Requests should be sent to{' '}
                        <a
                          href="mailto:contact@thebtree.com"
                          className="font-medium text-[#9B7A21] hover:text-[#0D1B2A] underline underline-offset-4"
                        >
                          contact@thebtree.com
                        </a>{' '}
                        with the following details:
                      </>
                    ) : (
                      paragraph
                    )}
                  </p>
                ))}

                {section.bullets && (
                  <ul className="space-y-2 text-slate-700 leading-7">
                    {section.bullets.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-3 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#C8A84B]" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {section.closing && (
                  <p className="text-slate-700 leading-7 mt-4">
                    {section.closing}
                  </p>
                )}
              </section>
            ))}
          </div>
        </article>
      </section>
    </main>
    </>
  );
};

export default RefundPolicy;
